import { Link } from "react-router-dom";
import { ArrowRight, Scale, Heart, Eye, Globe, Shield, FileText, Users } from "lucide-react";
import { Navigation, Footer, ScriptureBlock } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { MobileTooltip, TooltipProvider } from "@/components/MobileTooltip";
import { SEO } from "@/components/SEO";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { CovenantPanel } from "@/components/CovenantPanel";

const HERO_IMAGE = "https://images.unsplash.com/photo-1762609240822-a416849ce318?crop=entropy&cs=srgb&fm=jpg&q=85";

const processSteps = [
  {
    number: 1,
    title: "Intake & Goals",
    description: "Tell us about your property and what you're hoping to achieve.",
    tooltip: "We ask questions to understand your situation—timeline, goals, and any concerns. No pressure, just listening."
  },
  {
    number: 2,
    title: "Property Review",
    description: "We research your property and assess potential opportunities.",
    tooltip: "We check public records, zoning, utilities, and comparable sales. We share everything we find with you."
  },
  {
    number: 3,
    title: "Clear Opportunity Packaging",
    description: "We prepare a comprehensive package for potential buyers.",
    tooltip: "Your property details are packaged professionally with full disclosure of known facts—nothing hidden."
  },
  {
    number: 4,
    title: "Offer Collection",
    description: "We present your property to qualified builders and buyers.",
    tooltip: "The 'offer window' is a set time period where buyers submit their best offers. You see all of them."
  },
  {
    number: 5,
    title: "Closing Support",
    description: "We help facilitate a smooth closing process.",
    tooltip: "We coordinate with title companies, answer questions, and ensure everyone knows what's happening when."
  }
];

const scriptures = [
  {
    icon: Scale,
    verse: "A false balance is abomination to the Lord: but a just weight is his delight.",
    reference: "Proverbs 11:1",
    explanation: "We don't use hidden spreads or unclear pricing. You'll always know exactly what we earn."
  },
  {
    icon: Heart,
    verse: "Thou shalt love thy neighbour as thyself.",
    reference: "Matthew 22:39",
    explanation: "We treat you the way we'd want to be treated—with respect, patience, and honesty."
  },
  {
    icon: Eye,
    verse: "Providing for honest things, not only in the sight of the Lord, but also in the sight of men.",
    reference: "2 Corinthians 8:21",
    explanation: "We're transparent before God and you. Our compensation is disclosed in writing."
  },
  {
    icon: Globe,
    verse: "The earth is the Lord's, and the fulness thereof.",
    reference: "Psalm 24:1",
    explanation: "We're stewards of what God owns. Your land isn't just property—it's part of His creation."
  }
];

export default function SellerHome() {
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-white" data-testid="seller-home">
        <SEO 
          title="Sell Your Land Ethically | Transparent Land Sales"
          description="Sell your land with integrity through Steward Lots. Ethical land facilitation with full disclosure, transparent compensation, and no pressure tactics. Serving landowners in Ohio and nationwide."
          path="/seller"
        />
        <Navigation mode="seller" />

        {/* Hero Section */}
        <section className="relative pt-16 min-h-[60vh] flex items-center" data-testid="seller-hero">
          <img 
            src={HERO_IMAGE}
            alt="Rural land property - sell your land with integrity and full disclosure through ethical land stewardship"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-white/80" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
            <div className="max-w-2xl">
              <div className="flex items-center gap-4 mb-6">
                <span className="inline-block px-4 py-1 bg-[#BFA15F]/20 text-[#0F0F10] text-sm font-medium tracking-wide uppercase gentle-reveal">
                  Seller Mode
                </span>
                <div className="live-indicator text-xs text-[#57534E] gentle-reveal" style={{ animationDelay: '0.2s' }}>
                  <span className="live-dot" aria-hidden="true"></span>
                  <span>Accepting submissions</span>
                </div>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-[#1C1917] mb-6 gentle-reveal" style={{ animationDelay: '0.3s' }}>
                Sell Your Land with<br />
                <span className="text-[#BFA15F]">Integrity & Full Disclosure</span>
              </h1>
              <p className="text-lg text-[#57534E] mb-8 max-w-lg gentle-reveal" style={{ animationDelay: '0.5s' }}>
                We will tell the truth, disclose what we know, and treat you with dignity. No pressure. No manipulation. No deception.
              </p>
              <Link to="/sell/submit" aria-label="Submit your property for sale with integrity" className="gentle-reveal inline-block" style={{ animationDelay: '0.7s' }}>
                <Button 
                  size="lg" 
                  className="btn-enhanced bg-[#0F0F10] text-white hover:bg-[#1C1917] font-medium px-8"
                  data-testid="submit-property-btn"
                >
                  Submit Your Property
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Welcome Message */}
        <ScrollReveal animation="reveal-up">
          <section className="py-16 px-6 md:px-12 bg-[#FBF9F3]" data-testid="welcome-section">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block border-l-[3px] border-[#BFA15F] pl-4 text-left mb-6">
                <p className="font-serif italic text-[#57534E]">
                  "The earth is the Lord's, and the fulness thereof."
                </p>
                <p className="text-sm text-[#A8A29E] mt-1 uppercase tracking-wide">Psalm 24:1</p>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#1C1917] mb-4">
                Welcome, Steward of the Land
              </h2>
              <p className="text-[#57534E] max-w-2xl mx-auto">
                You've been entrusted with a portion of God's creation. Whether you're ready to sell or simply 
                exploring options, we're here to serve you with honesty, clarity, and respect for your dignity.
              </p>
            </div>
          </section>
        </ScrollReveal>

        {/* Covenant Transparency Panels for Sellers */}
        <ScrollReveal animation="reveal-up">
          <section className="py-16 px-6 md:px-12" data-testid="transparency-section">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4 text-center">
                How We Protect Land Sellers
              </h2>
              <p className="text-[#57534E] text-center mb-10 max-w-2xl mx-auto">
                Expand each section to see exactly how our Covenant Accountability Model serves your interests.
              </p>
              
              <div className="space-y-4">
                <CovenantPanel
                  title="How Your Property Is Evaluated"
                  scripture="Providing for honest things"
                  reference="2 Corinthians 8:21"
                  icon={FileText}
                >
                  <p>
                    We research your property using public records, comparable sales, zoning regulations, 
                    and utility availability. We share everything we discover with you—the good and the challenging.
                  </p>
                  <p>
                    Our evaluation helps you understand what your property might be worth to different 
                    types of buyers. We don't manufacture numbers to pressure you into a decision.
                  </p>
                  <p className="font-medium text-[#1C1917]">
                    Our commitment: You will know everything we know about your property's value and potential.
                  </p>
                </CovenantPanel>

                <CovenantPanel
                  title="How Your Interests Are Protected"
                  scripture="Thou shalt love thy neighbour as thyself"
                  reference="Matthew 22:39"
                  icon={Shield}
                >
                  <p>
                    Before any agreement is signed, you receive a clear written disclosure of our compensation 
                    structure. You'll know exactly how we make money on the transaction.
                  </p>
                  <p>
                    We explain our role honestly—including whether we plan to assign the contract to a 
                    builder or buyer. You deserve to understand who's involved and why.
                  </p>
                  <p className="font-medium text-[#1C1917]">
                    Our commitment: No hidden agendas, no undisclosed assignments, no surprises at closing.
                  </p>
                </CovenantPanel>

                <CovenantPanel
                  title="How We Connect You with Buyers"
                  scripture="A just weight is his delight"
                  reference="Proverbs 11:1"
                  icon={Users}
                >
                  <p>
                    We maintain relationships with qualified builders and land buyers who share our 
                    commitment to fair dealing. When we present your property, we attract serious buyers.
                  </p>
                  <p>
                    Offers are collected during a defined window, and you see all of them. We explain 
                    the pros and cons of each offer, but the decision is always yours.
                  </p>
                  <p className="font-medium text-[#1C1917]">
                    Our commitment: We facilitate connections, not pressure. Your timeline, your decision.
                  </p>
                </CovenantPanel>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* What This Is / Isn't */}
        <section className="py-24 px-6 md:px-12 bg-[#F5F5F4]" data-testid="what-this-is">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal animation="reveal-up">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] text-center mb-16">
                What This Is & What This Isn't
              </h2>
            </ScrollReveal>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
              {/* What It Is */}
              <ScrollReveal animation="reveal-up" delay={1}>
                <div className="bg-white p-8 border border-[#E7E5E4] interactive-card">
                  <h3 className="font-serif text-xl text-[#1C1917] mb-6 pb-4 border-b border-[#E7E5E4]">
                    What This Is
                  </h3>
                  <ul className="space-y-4 text-[#57534E]">
                    <li className="flex gap-3">
                      <span className="text-[#BFA15F]" aria-hidden="true">✓</span>
                      A stewardship-led land facilitation process
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#BFA15F]" aria-hidden="true">✓</span>
                      Full disclosure of everything we know about your property
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#BFA15F]" aria-hidden="true">✓</span>
                      Clear communication about our compensation
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#BFA15F]" aria-hidden="true">✓</span>
                      Respect for your time, decisions, and dignity
                    </li>
                  </ul>
                </div>
              </ScrollReveal>

              {/* What It Isn't */}
              <ScrollReveal animation="reveal-up" delay={2}>
                <div className="bg-white p-8 border border-[#E7E5E4] interactive-card">
                  <h3 className="font-serif text-xl text-[#1C1917] mb-6 pb-4 border-b border-[#E7E5E4]">
                    What This Isn't
                  </h3>
                  <ul className="space-y-4 text-[#57534E]">
                    <li className="flex gap-3">
                      <span className="text-[#A8A29E]" aria-hidden="true">✗</span>
                      High-pressure sales tactics
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#A8A29E]" aria-hidden="true">✗</span>
                      Manipulation or hidden agendas
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#A8A29E]" aria-hidden="true">✗</span>
                      Hidden spreads or unclear pricing
                    </li>
                    <li className="flex gap-3">
                      <span className="text-[#A8A29E]" aria-hidden="true">✗</span>
                      Rushing you into decisions you'll regret
                    </li>
                  </ul>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-6 md:px-12" data-testid="how-it-works">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal animation="reveal-up">
              <div className="text-center mb-16">
                <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-4">
                  How the Land Selling Process Works
                </h2>
                <p className="text-[#57534E] text-lg">
                  A simple, transparent 5-step process for land sellers. Tap or hover the{" "}
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-[#E7E5E4] rounded-full text-xs" aria-hidden="true">?</span>
                  {" "}for more details.
                </p>
              </div>
            </ScrollReveal>

            <div className="space-y-6 max-w-3xl mx-auto">
              {processSteps.map((step, index) => (
                <ScrollReveal key={step.number} animation="reveal-up" delay={index + 1}>
                  <article 
                    className="flex gap-6 items-start bg-[#F5F5F4] p-6 border border-[#E7E5E4] interactive-card"
                    data-testid={`process-step-${step.number}`}
                  >
                    <div className="w-12 h-12 bg-[#0F0F10] text-white flex items-center justify-center font-serif text-xl flex-shrink-0" aria-hidden="true">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-[#1C1917] text-lg">{step.title}</h3>
                        <MobileTooltip content={step.tooltip} id={`step-${step.number}`} />
                      </div>
                      <p className="text-[#57534E]">{step.description}</p>
                    </div>
                  </article>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why We Do It This Way */}
        <section className="py-24 px-6 md:px-12 bg-[#0F0F10]" data-testid="why-section">
          <div className="max-w-7xl mx-auto">
            <ScrollReveal animation="reveal-up">
              <div className="text-center mb-16">
                <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
                  Why We Treat Land Sellers This Way
                </h2>
                <p className="text-[#A8A29E] text-lg max-w-2xl mx-auto">
                  Our approach is rooted in Scripture—these aren't just nice ideas, they're commands we follow. 
                  Learn more about <Link to="/faith" className="text-[#BFA15F] hover:underline">our faith and operating principles</Link>.
                </p>
              </div>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {scriptures.map((item, index) => (
                <ScrollReveal key={index} animation="reveal-up" delay={index + 1}>
                  <article 
                    className="scripture-card bg-[#1a1a1a] p-8 border border-[#2a2a2a] hover:border-[#BFA15F]/30 transition-colors"
                    data-testid={`scripture-card-${index}`}
                  >
                    <item.icon className="w-8 h-8 text-[#BFA15F] mb-4" aria-hidden="true" />
                    <div className="border-l-[3px] border-[#BFA15F] pl-4 mb-4">
                      <p className="font-serif italic text-[#E7E5E4]">{item.verse}</p>
                      <p className="text-sm text-[#A8A29E] mt-1 uppercase tracking-wide">{item.reference}</p>
                    </div>
                    <p className="text-[#A8A29E]">{item.explanation}</p>
                  </article>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 md:px-12 bg-white" data-testid="seller-cta">
          <div className="max-w-4xl mx-auto text-center">
            <ScrollReveal animation="reveal-up">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">
                Ready to Sell Your Land with Integrity?
              </h2>
              <p className="text-[#57534E] text-lg mb-8 max-w-2xl mx-auto">
                Submit your property information and we'll be in touch within 24-48 hours with next steps. 
                If you're a <Link to="/builder" className="text-[#BFA15F] hover:underline">builder or land buyer</Link>, 
                visit our builder page instead.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/sell/submit" aria-label="Submit your property for ethical sale">
                  <Button 
                    size="lg" 
                    className="btn-enhanced bg-[#0F0F10] text-white hover:bg-[#1C1917] font-medium px-8"
                    data-testid="cta-submit-btn"
                  >
                    Submit Your Property
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact" aria-label="Contact us with questions">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-[#0F0F10] text-[#0F0F10] hover:bg-[#F5F5F4] font-medium px-8"
                  >
                    Ask a Question
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <Footer />
      </div>
    </TooltipProvider>
  );
}
